import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { getToken } from '../../services/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeader() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * useCabinCVCurrent — GET /cabin/cv/current
 * Returns the student's latest CV profile (draft or submitted).
 * 404 is treated as "no CV yet" and returns null data instead of throwing.
 */
export function useCabinCVCurrent() {
  return useQuery({
    queryKey: ['cabin', 'cv', 'current'],
    queryFn: async () => {
      try {
        const res = await axios.get(`${API}/cabin/cv/current`, {
          headers: authHeader(),
        });
        return res.data;
      } catch (err) {
        if (err?.response?.status === 404) {
          return null; // No CV yet — not an error
        }
        throw err;
      }
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!getToken(),
  });
}

/**
 * useSaveCVDraft — POST /cabin/cv/draft
 * Partial upsert for autosave.
 * Payload now includes top-level fields: unvan, hakkimda, is_deneyimleri, foto_url
 */
export function useSaveCVDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await axios.post(`${API}/cabin/cv/draft`, body, {
        headers: authHeader(),
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cabin', 'cv', 'current'] });
    },
  });
}

/**
 * useSubmitCV — PUT /cabin/cv
 * Full submit with kvkk_consent: true required.
 * Payload now includes top-level fields: unvan, hakkimda, is_deneyimleri, foto_url
 */
export function useSubmitCV() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await axios.put(`${API}/cabin/cv`, body, {
        headers: authHeader(),
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cabin', 'cv', 'current'] });
    },
  });
}

/**
 * useDeleteCV — DELETE /cabin/cv/me
 * KVKK soft-delete.
 */
export function useDeleteCV() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axios.delete(`${API}/cabin/cv/me`, {
        headers: authHeader(),
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cabin', 'cv', 'current'] });
    },
  });
}

/**
 * useUploadCvPhoto — POST /cabin/cv/photo (multipart/form-data)
 * Uploads a profile photo. Returns { foto_url }.
 * Max 2MB enforced at call site.
 */
export function useUploadCvPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/cabin/cv/photo`, formData, {
        headers: {
          ...authHeader(),
        },
      });
      return res.data; // { foto_url: "..." }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cabin', 'cv', 'current'] });
    },
  });
}

/**
 * useDeleteCvPhoto — DELETE /cabin/cv/photo (KVKK removal)
 */
export function useDeleteCvPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axios.delete(`${API}/cabin/cv/photo`, {
        headers: authHeader(),
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cabin', 'cv', 'current'] });
    },
  });
}
